import { useState } from "react";
import { useNavigate } from "react-router-dom";import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Smartphone, Mail } from "lucide-react";

export default function Login() {
  const { login, selectAccount, verify2FA, setup2FAEnroll, enable2FAEnroll } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [step, setStep] = useState("password"); // password | choose | 2fa | enroll
  const [tempToken, setTempToken] = useState("");
  const [tenants, setTenants] = useState([]);
  const [method, setMethod] = useState("");
  const [code, setCode] = useState("");

  // Forced 2FA enrollment at login (mandatory roles not yet enrolled)
  const [setupToken, setSetupToken] = useState("");
  const [enrollMethod, setEnrollMethod] = useState("totp");
  const [enrollSetup, setEnrollSetup] = useState(null); // {method, qr, secret} | {method:'email', sent_to}

  const finish = (u) => {
    toast.success("Welcome back");
    const dest = ["hire_manager", "hire_coordinator"].includes(u.role) ? "/hire-dashboard" : u.role === "service_technician" ? "/service-jobs" : u.role === "job_dispatcher" ? "/jobs" : "/";
    navigate(dest);
  };

  // Handles any login-step response: user (done) | tenant_choice_required | twofa_required | twofa_setup_required
  const handleResult = (data) => {
    if (data.user) return finish(data.user);
    if (data.tenant_choice_required) {
      setTempToken(data.temp_token);
      setTenants(data.tenants);
      setStep("choose");
      return;
    }
    if (data.twofa_setup_required) {
      setSetupToken(data.setup_token);
      setEnrollSetup(null);
      setEnrollMethod("totp");
      setStep("enroll");
      return;
    }
    if (data.twofa_required) {
      setTempToken(data.temp_token);
      setMethod(data.method);
      setStep("2fa");
      if (data.method === "email") toast.info("We emailed you a verification code.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      handleResult(await login(email, password));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pickAccount = async (userId) => {
    setError("");
    setLoading(true);
    try {
      handleResult(await selectAccount(tempToken, userId));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      finish(await verify2FA(tempToken, code));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEnroll = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await setup2FAEnroll(setupToken, enrollMethod);
      setEnrollSetup(data);
      setCode("");
      if (data.method === "email") toast.info(`Code sent to ${data.sent_to}`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      finish(await enable2FAEnroll(setupToken, enrollSetup.method, code.trim()));
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden lg:flex items-start justify-start bg-zinc-200 text-zinc-900 p-10 relative overflow-hidden">
        <img
          src="/signin-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative flex items-center gap-3">
          <img src="/logo.png" alt="Job Q" className="h-12 w-12 object-contain drop-shadow-md" />
          <div className="text-4xl font-black tracking-tight text-zinc-900 leading-none">
            Job Q
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <img src="/logo.png" alt="Job Q" className="h-12 w-12 rounded-lg bg-white p-1.5 object-contain mb-6 lg:hidden" />

          {step === "password" && (
            <>
              <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display">Sign in</h1>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-sm font-bold text-zinc-900 block mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="w-full px-4 py-2.5 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-zinc-900 block mb-1.5">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="w-full px-4 py-2.5 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
                    placeholder="••••••••"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 className="animate-spin" size={16} />}
                  Sign in
                </button>
              </form>
            </>
          )}

          {step === "choose" && (
            <>
              <h1 className="font-heading text-3xl font-black tracking-tighter text-brand-display">Choose account</h1>
              <p className="text-zinc-500 mt-2 text-sm">This email exists in multiple tenants. Pick which one to sign in to.</p>
              <div className="mt-6 space-y-2" data-testid="tenant-choice-list">
                {tenants.map((t) => (
                  <button
                    key={t.user_id}
                    onClick={() => pickAccount(t.user_id)}
                    disabled={loading}
                    data-testid={`tenant-choice-${t.user_id}`}
                    className="w-full flex items-center justify-between border border-zinc-300 hover:border-brand hover:bg-brand-card-blue transition-colors px-4 py-3 text-left disabled:opacity-60"
                  >
                    <span className="font-bold text-zinc-900">{t.tenant_name}</span>
                    <span className="text-xs uppercase tracking-wider text-zinc-500">{t.role}</span>
                  </button>
                ))}
              </div>
              {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">{error}</div>}
            </>
          )}

          {step === "2fa" && (
            <>
              <h1 className="font-heading text-3xl font-black tracking-tighter text-brand-display">Verify it's you</h1>
              <p className="text-zinc-500 mt-2 text-sm">
                {method === "email"
                  ? "Enter the 6-digit code we emailed you."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
              <form onSubmit={submit2FA} className="mt-8 space-y-5">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="login-2fa-input"
                  inputMode="numeric"
                  placeholder="123456"
                  className="w-full px-4 py-2.5 border border-zinc-300 rounded-none text-sm tracking-[0.4em] font-mono text-center focus:outline-none focus:ring-2 focus:ring-brand"
                />
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">{error}</div>}
                <button
                  type="submit"
                  disabled={loading || !code.trim()}
                  data-testid="login-2fa-submit"
                  className="w-full bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 className="animate-spin" size={16} />}
                  Verify
                </button>
              </form>
            </>
          )}

          {step === "enroll" && (
            <>
              <h1 className="font-heading text-3xl font-black tracking-tighter text-brand-display">Secure your account</h1>
              <p className="text-zinc-500 mt-2 text-sm">
                Your role requires two-factor authentication. Set it up now to finish signing in.
              </p>

              {!enrollSetup && (
                <div className="mt-6 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setEnrollMethod("totp")}
                      data-testid="enroll-method-totp"
                      className={`flex items-center gap-2 border p-3 text-sm font-semibold transition-colors ${
                        enrollMethod === "totp" ? "border-brand bg-brand-card-blue" : "border-zinc-300 hover:bg-zinc-50"
                      }`}
                    >
                      <Smartphone size={18} className="text-brand" /> Authenticator app
                    </button>
                    <button
                      type="button"
                      onClick={() => setEnrollMethod("email")}
                      data-testid="enroll-method-email"
                      className={`flex items-center gap-2 border p-3 text-sm font-semibold transition-colors ${
                        enrollMethod === "email" ? "border-brand bg-brand-card-blue" : "border-zinc-300 hover:bg-zinc-50"
                      }`}
                    >
                      <Mail size={18} className="text-brand" /> Email code
                    </button>
                  </div>
                  {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">{error}</div>}
                  <button
                    onClick={startEnroll}
                    disabled={loading}
                    data-testid="enroll-continue-button"
                    className="w-full bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="animate-spin" size={16} />}
                    Continue
                  </button>
                </div>
              )}

              {enrollSetup && (
                <form onSubmit={submitEnroll} className="mt-6 space-y-4">
                  {enrollSetup.method === "totp" && (
                    <div>
                      <p className="text-sm text-zinc-600 mb-2">
                        Scan this QR code with Google Authenticator, Microsoft Authenticator or Authy, then enter the 6-digit code.
                      </p>
                      <img src={enrollSetup.qr} alt="2FA QR code" data-testid="enroll-qr" className="h-44 w-44 border border-zinc-200" />
                      <p className="text-xs text-zinc-400 mt-2 break-all">
                        Or enter this key manually: <span className="font-mono text-zinc-600">{enrollSetup.secret}</span>
                      </p>
                    </div>
                  )}
                  {enrollSetup.method === "email" && (
                    <p className="text-sm text-zinc-600">
                      We emailed a 6-digit code to <b>{enrollSetup.sent_to}</b>. Enter it below to finish.
                    </p>
                  )}
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="enroll-code-input"
                    inputMode="numeric"
                    placeholder="123456"
                    className="w-full px-4 py-2.5 border border-zinc-300 rounded-none text-sm tracking-[0.4em] font-mono text-center focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">{error}</div>}
                  <button
                    type="submit"
                    disabled={loading || !code.trim()}
                    data-testid="enroll-verify-button"
                    className="w-full bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="animate-spin" size={16} />}
                    Verify &amp; finish
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
